import { useEffect, useRef, useState } from "react";
import TravelPreferencesModal from "../../Modals/demoAIModal";
import TravelPreferencesForm from "./TravelPreferencesForm";
import TravelPreferencesResult from "./TravelPreferencesResult";
import DataLoader from "../../../Utils/DataLoader";
import "./style.css";
import TravelPreferencesBanner from "./TravelPreferencesBanner";

export default function MainTravelPreferences() {
    const localFormData = JSON.parse(localStorage.getItem("localFormData")) || null;

    const [formData, setFormData] = useState(localFormData);
    const [isSubmitted, setIsSubmitted] = useState(
        Boolean(localFormData?.isSubmitted)
    );
    const [tripData, setTripData] = useState({});
    const [loading, setLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    const [hasStartedStreaming, setHasStartedStreaming] = useState(false);


    const eventSourceRef = useRef(null);
    const isStreamCompletedRef = useRef(false);
    const isManuallyClosedRef = useRef(false);

    const handleFormSubmit = (data) => {
        // 🔁 Cleanup previous stream
        if (eventSourceRef.current) {
            isManuallyClosedRef.current = true;
            eventSourceRef.current.close();
        }

        isStreamCompletedRef.current = false;
        isManuallyClosedRef.current = false;

        setFormData(data);
        setLoading(true);
        setHasStartedStreaming(false);
        setTripData({ days: [] });
        setIsSubmitted(false);

        const queryParams = new URLSearchParams(data).toString();
        const url = `https://mysittivacations.kindlebit.com/generate-itinerary/stream?${queryParams}`;

        let partialTripData = { days: [] };

        const es = new EventSource(url);
        eventSourceRef.current = es;

        es.onmessage = (event) => {
            try {
                const jsonChunk = JSON.parse(event.data);

                // ✅ stream completed
                if (jsonChunk?.content === "completed") {
                    isStreamCompletedRef.current = true;
                    setLoading(false);
                    setIsSubmitted(true);
                    es.close();
                    return;
                }

                const days = jsonChunk?.content?.days;
                if (!Array.isArray(days)) return;

                setHasStartedStreaming(true); // 👈 STREAM HAS BEGUN

                setTripData((prev) => {
                    const updatedDays = [...(prev.days || [])];

                    days.forEach((day, index) => {
                        updatedDays[index] = {
                            dayNumber: day.dayNumber,
                            dayTitle: day.dayTitle,
                            highlights: day.highlights || [],
                            mustSee: day.mustSee || [],
                            foodPicks: day.foodPicks || [],
                            tip: day.tip || "",
                            estimatedSpend: day.estimatedSpend || ""
                        };
                    });

                    return { days: updatedDays };
                });

            } catch (err) {
                console.warn("Invalid SSE data:", event.data);
            }
        };

        es.onerror = (event) => {
            // 🟢 Ignore normal closure
            if (
                isStreamCompletedRef.current ||
                isManuallyClosedRef.current ||
                es.readyState === EventSource.CLOSED
            ) {
                console.log("SSE closed normally");
                return;
            }

            // 🔴 Real error
            console.error("SSE error:", event);
            es.close();
            setLoading(false);
            setShowToast(true);
            setToastMessage("Streaming failed. Try again.");
        };
    };

    // 🔁 Resume streaming if localStorage exists
    useEffect(() => {
        if (localFormData && localFormData.isSubmitted && !tripData?.days?.length) {
            handleFormSubmit(localFormData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ⏳ Auto hide toast
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => setShowToast(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // 🧹 Cleanup on unmount
    useEffect(() => {
        return () => {
            isManuallyClosedRef.current = true;
            eventSourceRef.current?.close();
        };
    }, []);

    return (
        <div>
            {/* Toast */}
            {showToast && (
                <div
                    className="toast align-items-center text-bg-danger border-0 show position-fixed fade-in"
                    style={{ top: 20, right: 20, zIndex: 9999 }}
                    role="alert"
                >
                    <div className="toast-body">{toastMessage}</div>
                </div>
            )}
            <TravelPreferencesBanner />
            {!hasStartedStreaming ? (
                <TravelPreferencesForm onSubmit={handleFormSubmit} disabled={false} />
            ) : (
                <>
                    {loading && (
                        <div
                            className="toast align-items-center text-bg-info border-0 show position-fixed fade-in"
                            style={{ top: 20, right: 20, zIndex: 9999, backgroundColor: "#efdd5a", borderRadius: '10px' }}
                            role="alert"
                        >
                            <div className="toast-body" style={{ backgroundColor: "#efdd5a", borderRadius: '10px' }}>
                                <span className="me-2" style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                                Generating your itinerary…
                            </div>
                        </div>
                    )}

                    <style>{`
                        @keyframes spin {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }
                    `}</style>

                    <TravelPreferencesResult
                        formData={formData}
                        tripData={tripData}
                        onBack={() => {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                            setHasStartedStreaming(false);
                            setLoading(false);
                            setTripData({});
                            setFormData(null);
                            localStorage.removeItem("localFormData");
                        }}
                    />
                </>
            )}


            {/* Modal stays mounted always */}
            <TravelPreferencesModal formData={formData} />
        </div>
    );
}
